import { FormSectionHandler } from '../entity.handler';
import { UserResponse, UserUpdateRequest, UserClientPermissions } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderDefinition, FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, ClientSelectField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as _ from 'lodash';
import { IdName } from 'app/shared/graphql';
import * as Bluebird from 'bluebird';

interface Permissions extends UserClientPermissions {
    client: IdName;
}

interface EditorValues {
    clients?: ListFieldValue<Permissions>[];
}

export class UserClientPermissionsSectionHandler implements FormSectionHandler<UserResponse, EditorValues> {

    type = 'form';
    icon = 'clipboard-list';
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    properties = ['clients'];

    modifiedClients: { [key: string]: 'deleted' | 'edited' | 'created' } = {};

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.USER.CLIENT_PERMISSIONS.${key}`, values);
    }

    applyUpdate(entity: UserResponse, payload: UserUpdateRequest): Promise<UserResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateUser(entity.id, payload));
    }

    async mapEntityToEditor(entity: UserResponse): Promise<EditorValues> {
        this.modifiedClients = {};
        const result: EditorValues = {
            clients: await Bluebird.map(Object.keys(entity.permissions?.clients || {}), async clientId => {
                const client = await this.admin.getCached(`getClient/${clientId}`, api => api.accounts.getClient(clientId)).catch(() => ({ id: clientId, name: clientId }));
                const item = {
                    ...entity.permissions.clients[clientId],
                    client: {
                        id: client.id,
                        name: client.name,
                    },
                };
                return this.permissionsToListItem(item);
            }, { concurrency: 3 }),
        };
        return result;
    }

    async mapEditorToEntity(entity: UserUpdateRequest, values: EditorValues): Promise<UserUpdateRequest> {
        if (values.clients) {
            entity.permissions = entity.permissions || {};
            entity.permissions.clients = Object.entries(this.modifiedClients).reduce((obj, [id, action]) => {
                if (action === 'deleted') {
                    obj[id] = null;
                } else {
                    const { client, ...data } = values.clients.find(x => x.id === id).data;
                    obj[id] = data;
                }
                return obj;
            }, {} as any);
        }
        return entity;
    }

    async getFields(entity: UserResponse): Promise<FormBuilderField[]> {
        const fields: FormBuilderField[] = [
            {
                id: 'clients', title: this.translate('FIELDS.CLIENTS'), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddClientModal.bind(this), },
                    { id: 'edit', click: this.showAddClientModal.bind(this), },
                    { id: 'delete', click: this.markDeletedItem.bind(this) },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ];
        return EntityUtils.prepareBulk(!entity, ['clients'], fields);
    }

    async markDeletedItem(_action: string, _field: FormBuilderField, _values: EditorValues, item?: ListFieldValue<any>): Promise<boolean> {
        this.modifiedClients[item.id] = 'deleted';
        return true;
    }

    mapValidationErrors(errors: { [field: string]: string }): { [field: string]: string } {

        const result = {};
        Object.keys(errors).forEach(field => {
            this.properties.forEach(prop => {
                if (field.startsWith(prop)) {
                    result[prop] = result[prop] || [];
                    result[prop].push(errors[field]);
                }
            });
        });
        return Object.keys(result).reduce((obj, key) => { obj[key] = result[key].join(', '); return obj; }, {});
    }

    permissionsToListItem(permissions: Permissions): ListFieldValue<Permissions> {
        const desc = '';
        return {
            id: permissions.client.id,
            name: permissions.client.name,
            icon: 'house',
            description: desc,
            data: permissions,
        };
    }

    updateOrAddItem<T extends { client: IdName }>(items: ListFieldValue<any>[], existingItem: ListFieldValue<any>, data: T, itemToListItem: (data: T) => ListFieldValue<T>) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = itemToListItem(data);
        } else {
            items.push(itemToListItem(data));
            console.log(items);
        }
    }

    async showAddClientModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<Permissions> {


        let form: FormBuilderDefinition = {
            groups: [{
                name: this.translate('MODALS.CLIENT.TITLE'),
                description: this.translate('MODALS.CLIENT.DESC'),
                fields: [
                    {
                        ...ClientSelectField('client', this.translate('FIELDS.CLIENT'), true, this.ownerId), readonly: !!item,
                        onChange: async (_f: FormBuilderField, vals: any) => {
                            // Do some black magic to make the assets list only appear after a client is selected. We need this as we need the client ID for the assetfilter. The code
                            // below pushes the assetfilter field, sets the client to readonly and then updates the form value so that angular fires ngOnChanges on the form buider.
                            if (vals.client) {
                                form.groups[0].fields[0].readonly = true;
                                form.groups[0].fields.push({ id: 'assets', title: this.translate('FIELDS.ASSETS'), type: 'assetfilter', required: true, options: { ownerId: vals.client.id, allowedAssetTypes: ['asset'] } });
                                form = { ...form }; // make sure the reference gets updated to trigger change detection
                            }
                        },
                    },
                    item ? { id: 'assets', title: this.translate('FIELDS.ASSETS'), type: 'assetfilter', required: true, options: { ownerId: item.data.client?.id, allowedAssetTypes: ['asset'] } } : null,
                ].filter(x => x),
            }],
        };


        return this.admin.showFormModal<Permissions>(form, item?.data || {
            client: undefined,
            assets: [],
        }).then(permissions => {
            if (permissions) {
                this.modifiedClients[permissions.client.id] = item ? 'edited' : 'created';
                this.updateOrAddItem(values.clients, item, permissions, this.permissionsToListItem.bind(this));
            }
            return permissions;
        });

    }

}

